import { forwardRef } from 'react';

import { Button, IconButton } from 'components/Button';
import { WarningCircleIcon, XIcon } from 'components/Icon';
import { ProgressBar } from 'components/ProgressBar';
import { cn } from 'components/utils';

import {
  fileUploadItemActionsRecipe,
  fileUploadItemBodyRecipe,
  fileUploadItemMetaRecipe,
  fileUploadItemNameRecipe,
  fileUploadItemPercentRecipe,
  fileUploadItemPreviewRecipe,
  fileUploadItemRecipe,
} from './FileUpload.recipe';

import type { FileUploadItemProps } from './types';

/**
 * One chosen file, from upload to done. Pairs with `FileUpload`.
 *
 * Every status is the same height, so nothing jumps as a row moves from uploading to uploaded. A
 * failed row keeps its place and puts Try again first, because it is the likely next step.
 *
 * `preview` is a slot: a `FilePreview` for images, video and documents, or an `Avatar` at size 40 —
 * Person for a profile picture, Organisation for a logo, which is never cropped. Give the Avatar
 * `decorative`, because the file name beside it already says what it is.
 */
export const FileUploadItem = forwardRef<HTMLDivElement, FileUploadItemProps>((props, ref) => {
  const { name, preview, status, disabled, className, 'data-testid': testId } = props;

  return (
    <div
      ref={ref}
      className={cn(fileUploadItemRecipe({ status }), className)}
      data-status={status}
      data-testid={testId}
    >
      <span className={fileUploadItemPreviewRecipe()} data-part="preview">
        {preview}
      </span>
      <span className={fileUploadItemBodyRecipe()}>
        <span className={fileUploadItemNameRecipe()} title={name}>
          {name}
        </span>
        <span className={fileUploadItemMetaRecipe({ status })} data-part="meta">
          {props.status === 'uploading' && (
            <>
              <ProgressBar value={props.progress} size="sm" aria-label={props.progressLabel} className="flex-1" />
              <span className={fileUploadItemPercentRecipe()} aria-hidden>
                {`${Math.round(Math.min(100, Math.max(0, props.progress)))}%`}
              </span>
            </>
          )}
          {props.status === 'uploaded' && props.details && <span>{props.details}</span>}
          {props.status === 'failed' && (
            <>
              <WarningCircleIcon className="shrink-0" />
              <span className="truncate" role="alert">
                {props.error}
              </span>
            </>
          )}
        </span>
      </span>
      {!disabled && (
        <span className={fileUploadItemActionsRecipe()}>
          {props.status === 'uploaded' && props.onReplace && props.replaceLabel && (
            <Button type="button" appearance="tertiary" size="xs" onClick={props.onReplace}>
              {props.replaceLabel}
            </Button>
          )}
          {props.status === 'failed' && (
            <Button type="button" appearance="secondary" size="xs" onClick={props.onRetry}>
              {props.retryLabel}
            </Button>
          )}
          {props.status === 'uploading' ? (
            <IconButton
              type="button"
              appearance="tertiary"
              size="xs"
              aria-label={props.cancelLabel}
              onClick={props.onCancel}
            >
              <XIcon />
            </IconButton>
          ) : (
            <IconButton
              type="button"
              appearance="tertiary"
              size="xs"
              aria-label={props.removeLabel}
              onClick={props.onRemove}
            >
              <XIcon />
            </IconButton>
          )}
        </span>
      )}
    </div>
  );
});
